import { Alert, Radio, Stack, Text, Textarea, TextInput } from "@mantine/core";
import {
  INTRO_MAX_MARK_LENGTH,
  INTRO_MAX_WORD_LENGTH,
  INTRO_MAX_WORDS,
  initialsOf,
  introSchema,
  type Brand,
  type WelcomeIntro,
} from "@how/website-core";

/**
 * Website → Look, "How your site opens" (ADR 0073, amended 2026-10-14, "Welcome Home"): nothing, a short dark screen
 * with a spinner, or the church's own welcome words. Only the Revival theme plays either; the choice is saved with
 * the rest of the look.
 *
 * The words are typed one per line. The holds (how long each stays) are not asked for: the theme's own pacing
 * reads well for any list, and a church that wants its own can ask DCX support. What is kept in the brand is exactly
 * `WelcomeIntro`, checked by `introSchema` before Save.
 */

/** The form's own state: the words as typed, before they are a list. */
export interface IntroDraft {
  readonly style: "off" | "spinner" | "welcome";
  readonly line: string;
  readonly mark: string;
  readonly words: string;
  readonly mobileWords: string;
  readonly finalWord: string;
}

const lines = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

/** The draft a stored brand opens with. */
export function introDraftOf(intro: Brand["intro"]): IntroDraft {
  if (intro === false) {
    return { style: "off", line: "", mark: "", words: "", mobileWords: "", finalWord: "" };
  }
  if (intro.style === "spinner") {
    return { style: "spinner", line: "", mark: "", words: "", mobileWords: "", finalWord: "" };
  }
  return {
    style: "welcome",
    line: intro.line,
    mark: intro.mark ?? "",
    words: intro.words.join("\n"),
    mobileWords: intro.mobileWords?.join("\n") ?? "",
    finalWord: intro.finalWord ?? "",
  };
}

/**
 * What the draft saves, or why it cannot: `false` for off, the spinner, or the welcome words once they pass the
 * same check the public site applies. An optional field left blank is left out, so the theme fills it in.
 */
export function introOfDraft(draft: IntroDraft): { intro: Brand["intro"] } | { problem: string } {
  if (draft.style === "off") return { intro: false };
  if (draft.style === "spinner") return { intro: { style: "spinner" } };
  const words = lines(draft.words);
  const mobileWords = lines(draft.mobileWords);
  const candidate: Record<string, unknown> = {
    style: "welcome",
    line: draft.line.trim(),
    words,
  };
  if (draft.mark.trim().length > 0) candidate["mark"] = draft.mark.trim();
  if (mobileWords.length > 0) candidate["mobileWords"] = mobileWords;
  if (draft.finalWord.trim().length > 0) candidate["finalWord"] = draft.finalWord.trim();
  if (candidate["line"] === "") return { problem: "Type the first line, such as Welcome." };
  if (words.length === 0) return { problem: "Type at least one word for the second line." };
  if (words.length > INTRO_MAX_WORDS || mobileWords.length > INTRO_MAX_WORDS) {
    return { problem: `Up to ${String(INTRO_MAX_WORDS)} words, one per line.` };
  }
  const long = [draft.line, ...words, ...mobileWords, draft.finalWord].find(
    (w) => w.trim().length > INTRO_MAX_WORD_LENGTH,
  );
  if (long !== undefined) {
    return { problem: `Each line is up to ${String(INTRO_MAX_WORD_LENGTH)} letters: "${long}".` };
  }
  if (draft.mark.trim().length > INTRO_MAX_MARK_LENGTH) {
    return { problem: `The letters in the circle are up to ${String(INTRO_MAX_MARK_LENGTH)}.` };
  }
  const parsed = introSchema.safeParse(candidate);
  if (!parsed.success || parsed.data === false) {
    return { problem: "Check the words and try again." };
  }
  return { intro: parsed.data as WelcomeIntro };
}

export function IntroControls({
  draft,
  siteName,
  disabled,
  onChange,
}: {
  readonly draft: IntroDraft;
  readonly siteName: string;
  readonly disabled: boolean;
  readonly onChange: (draft: IntroDraft) => void;
}) {
  const outcome = introOfDraft(draft);
  const initials = initialsOf(siteName);
  return (
    <Stack gap="sm">
      <Radio.Group
        value={draft.style}
        onChange={(value) => {
          onChange({ ...draft, style: value as IntroDraft["style"] });
        }}
      >
        <Stack gap="xs">
          <Radio
            value="off"
            disabled={disabled}
            label="Straight to the page"
            description="Visitors see your home page at once."
          />
          <Radio
            value="spinner"
            disabled={disabled}
            label="A dark screen with a small spinner"
            description="About a second, once per visit, then the page fades in."
          />
          <Radio
            value="welcome"
            disabled={disabled}
            label="Your welcome words"
            description="A dark screen with your first line and a few words that change, then your home page comes in. Once per visit, shorter on a return."
          />
        </Stack>
      </Radio.Group>
      {draft.style !== "welcome" ? null : (
        <>
          <TextInput
            label="First line"
            description="In capitals on the screen, whatever you type here."
            placeholder="Welcome"
            maxLength={INTRO_MAX_WORD_LENGTH}
            disabled={disabled}
            value={draft.line}
            onChange={(event) => {
              onChange({ ...draft, line: event.currentTarget.value });
            }}
          />
          <Textarea
            label="Words that follow, one per line"
            description={`The second line shows each in turn and stays on the last. Up to ${String(INTRO_MAX_WORDS)}, each up to ${String(INTRO_MAX_WORD_LENGTH)} letters.`}
            placeholder={"to life\nas you are\nto the family\nhome"}
            autosize
            minRows={3}
            maxRows={INTRO_MAX_WORDS}
            disabled={disabled}
            value={draft.words}
            onChange={(event) => {
              onChange({ ...draft, words: event.currentTarget.value });
            }}
          />
          <TextInput
            label="Letters in the circle"
            description={`A round mark sits beside the words. Leave blank for your church's initials${initials.length === 0 ? "" : ` (${initials})`}.`}
            placeholder={initials.length === 0 ? "ABC" : initials}
            maxLength={INTRO_MAX_MARK_LENGTH}
            disabled={disabled}
            value={draft.mark}
            onChange={(event) => {
              onChange({ ...draft, mark: event.currentTarget.value });
            }}
          />
          <Textarea
            label="On a phone, one per line (optional)"
            description="A shorter list for small screens. Leave blank to show the first two words and the last."
            autosize
            minRows={2}
            maxRows={INTRO_MAX_WORDS}
            disabled={disabled}
            value={draft.mobileWords}
            onChange={(event) => {
              onChange({ ...draft, mobileWords: event.currentTarget.value });
            }}
          />
          <TextInput
            label="The one word a returning visitor sees (optional)"
            description="On a second visit in the same session the intro is shorter and shows one word. Leave blank for your last word."
            placeholder="Home"
            maxLength={INTRO_MAX_WORD_LENGTH}
            disabled={disabled}
            value={draft.finalWord}
            onChange={(event) => {
              onChange({ ...draft, finalWord: event.currentTarget.value });
            }}
          />
          {"problem" in outcome ? (
            <Alert variant="light" color="yellow" title="Not ready to save">
              {outcome.problem}
            </Alert>
          ) : null}
          <Text size="xs" c="dimmed">
            Visitors who have asked their device for less motion, or who arrive part-way down a
            page, go straight to the page. Anyone can skip it with a tap or the Escape key.
          </Text>
        </>
      )}
    </Stack>
  );
}
